import { useCallback, useEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useBoolean } from 'usehooks-ts';

import {
	ContactsSelectWithSearch,
	Text,
	Modal,
	spacing,
	Button,
	HeaderBackButton,
	CheckBox,
	Toast,
	AutoGrowInput,
} from '@pletly/ui';
import { MAX_MEDIA_COUNT, getName } from '@pletly/shared';

import { useAuthHeaders } from '@pletly/feature-auth';
import { BlurFaces } from '@pletly/feature-auto-blur';

import { useOrganisationInfo } from '@pletly/feature-organisation';
import { useUser } from '%hooks';
import { UserAvatar } from '%components';
import { Scope, useNavigationScope } from '%context';
import { Env } from '@env';

import { ImagePickerCollage } from '%components/ImagePickerCollage';

import { PostType } from '%__generated__/types';
import { RootStackParamList } from '%navigation/types';
import { PrivacyStatus } from '%constants/PrivacyStatus';
import { useNewPost } from '../../new/useNewPost';
import { useNewPostPrivacy } from '../../new/useNewPostPrivacy';
import { PostPrivacyButtonWeb } from '../../new/components/PostPrivacyButtonWeb';
import { ScheduleButtonWeb } from '../../new/components/ScheduleButtonWeb';
import { useNewPostErrors } from '../../new/useNewPostErrors';
import { PostFormProps } from './types';
import { TeamSelect } from './TeamSelect';
import { AllStaffSelect } from './AllStaffSelect/AllStaffSelect';
import BlurFaceIcon from './face.svg';
import { FilePicker } from './FilePicker';
import { Schedule } from './Schedule';
import { useScheduleForm } from './Schedule/useScheduleForm';

export const PostForm = ({
	postTypeParams,
	postToEdit,
	mutationOptions,
	onSaveStarted,
	onClose,
}: PostFormProps) => {
	const { canGoBack, goBack, replace } =
		useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const { t } = useTranslation();
	const user = useUser();
	const { getHeaders } = useAuthHeaders();
	const {
		isSelectUsersDisplayed,
		toggleSelectUsersDisplayed,
		careRecipients,
		selectedUsersIds,
		setSelectedUsersIds,
		selectedAllStaff,
		setAllStaffSelected,
		selectedTeamIds,
		setSelectedTeamsIds,
		selectedUsersInfoText = '',
		isPrivacyValid,
		isInternal,
		setInternalOnly,
	} = useNewPostPrivacy(
		(postTypeParams.type === PostType.Feed && postTypeParams.selectedUsers) ||
			postToEdit?.selectedUsers?.map(({ user }) => user.id),
		postToEdit?.status === PrivacyStatus.SelectedUsersStaff ? true : undefined,
	);
	const {
		screenTitle: postFormTitle,
		contentInput,
		selectedImages,
		setSelectedImages,
		blurredImages,
		setBlurredImages,
		onNewImage,
		onImageRemove,
		pickedFiles,
		pickFiles,
		onRemoveFile,
		saving,
		loading,
		onSavePress,
		isFormValid,
		isInformationPost,
		isCommunityPost,
		isPostOnUserFeed,
		scheduleDate,
		setScheduleDate,
	} = useNewPost({ postTypeParams, postToEdit, mutationOptions, isInternal });

	const { organisationInfo } = useOrganisationInfo();
	const { activeScope } = useNavigationScope();

	const showRestrictVisibilityOption = user.role !== 'USER' && !!organisationInfo;

	const showPrivacy = !isPostOnUserFeed && !isInformationPost && !isCommunityPost;
	const showSchedule =
		Env.ENABLE_POST_SCHEDULE &&
		!postToEdit?.publishedAt &&
		!isPostOnUserFeed &&
		!isInformationPost &&
		!isCommunityPost &&
		organisationInfo?.role &&
		activeScope === Scope.MainApp;

	const {
		value: isBlurFacesDisplayed,
		toggle: toggleBlurFaces,
		setFalse: closeAutoBlur,
	} = useBoolean(false);

	const {
		value: isScheduleDisplayed,
		setTrue: displaySchedule,
		setFalse: closeSchedule,
	} = useBoolean(false);

	useEffect(() => {
		if (postToEdit?.hasPublicationError) {
			Toast.show({
				type: 'warning',
				text1: t('post.publication-error-warning'),
				autoHide: false,
			});
		}
	}, [postToEdit?.hasPublicationError, t]);

	const scheduleFormMethods = useScheduleForm({
		defaultValues: scheduleDate ? { date: scheduleDate, time: scheduleDate } : undefined,
	});

	const handleGoBack = useCallback(() => {
		canGoBack() ? goBack() : replace('Home');
	}, [canGoBack, goBack, replace]);

	const onBlurAccept = useCallback(() => {
		setSelectedImages((currentlySelected) =>
			currentlySelected.map((media, mediaIndex) => {
				const blurred = blurredImages
					.filter(Boolean)
					.find((blurredImage) => blurredImage.mediaIndex === mediaIndex);

				if (!blurred) {
					return media;
				}

				return blurred;
			}),
		);
		setBlurredImages([]);

		closeAutoBlur();
	}, [blurredImages, closeAutoBlur, setBlurredImages, setSelectedImages]);

	const { errors, validate } = useNewPostErrors({
		isContentValid: isFormValid,
		isPrivacyValid: !showPrivacy || isPrivacyValid,
	});

	const contacts = useMemo(
		() =>
			careRecipients?.map((careRecipient) => ({
				...careRecipient,
				photoSource: careRecipient.photoUrl
					? { uri: careRecipient.photoUrl, headers: getHeaders(careRecipient.photoUrl) }
					: undefined,
			})),
		[careRecipients, getHeaders],
	);

	const defaultReferenceImage =
		selectedUsersIds?.length === 1
			? (contacts.find(({ id }) => id === selectedUsersIds[0])?.photoUrl ?? undefined)
			: undefined;

	const handleSavePress = useCallback(() => {
		const isValid = validate();

		if (!isValid) {
			return;
		}

		void onSavePress({ selectedUsersIds, selectedTeamIds, selectedAllStaff });
		onSaveStarted?.();
	}, [validate, onSavePress, selectedUsersIds, selectedTeamIds, selectedAllStaff, onSaveStarted]);

	const actionsForm = useMemo(
		() => (
			<Button
				style={styles.publishButton}
				variant="filled"
				onPress={handleSavePress}
				color="primary"
				disabled={saving}
				testID="post-publish-button"
			>
				{t('post.new.publish')}
			</Button>
		),
		[t, handleSavePress, saving],
	);

	const actionsPrivacy = useMemo(
		() => (
			<>
				<Button
					variant="filled"
					onPress={toggleSelectUsersDisplayed}
					color="primary"
					disabled={!isPrivacyValid}
					style={styles.privacyButton}
				>
					{t('post.new.select')}
				</Button>

				<Button onPress={toggleSelectUsersDisplayed} color="primary" style={styles.privacyButton}>
					{t('common.back')}
				</Button>
			</>
		),
		[t, toggleSelectUsersDisplayed, isPrivacyValid],
	);

	const actionsBlurFaces = useMemo(
		() => (
			<>
				<Button onPress={handleGoBack} color="primary" style={styles.privacyButton}>
					{t('common.cancel')}
				</Button>
				<Button
					variant="filled"
					onPress={onBlurAccept}
					color="primary"
					style={styles.privacyButton}
				>
					{t('post.accept')}
				</Button>
			</>
		),
		[handleGoBack, t, onBlurAccept],
	);

	const handleScheduleSave = useCallback(() => {
		void scheduleFormMethods.handleSubmit(({ time }) => {
			setScheduleDate(time);
			closeSchedule();
		})();
	}, [scheduleFormMethods, closeSchedule, setScheduleDate]);

	const actionsSchedule = useMemo(
		() => (
			<>
				<Button
					variant="filled"
					onPress={handleScheduleSave}
					color="primary"
					style={styles.privacyButton}
				>
					{t('post.new.select')}
				</Button>

				<Button onPress={closeSchedule} color="primary" style={styles.privacyButton}>
					{t('common.back')}
				</Button>
			</>
		),
		[t, closeSchedule, handleScheduleSave],
	);

	return (
		<Modal
			visible={!loading}
			title={
				isBlurFacesDisplayed ? (
					<HeaderBackButton onPress={toggleBlurFaces} />
				) : isSelectUsersDisplayed ? (
					t('post.new.publication-settings')
				) : isScheduleDisplayed ? (
					<HeaderBackButton onPress={closeSchedule} />
				) : (
					postFormTitle
				)
			}
			onClose={onClose ?? handleGoBack}
			actions={
				isSelectUsersDisplayed
					? actionsPrivacy
					: isBlurFacesDisplayed
						? actionsBlurFaces
						: isScheduleDisplayed
							? actionsSchedule
							: actionsForm
			}
			style={styles.modal}
		>
			{isSelectUsersDisplayed ? (
				<View style={{ gap: spacing(3) }}>
					{contacts.length > 0 && (
						<ContactsSelectWithSearch
							contacts={contacts}
							mode="multi"
							selectedIds={selectedUsersIds}
							setSelectedIds={setSelectedUsersIds}
							searchLabel={t('post.new.search-care-recipients')}
							listTitle={t('post.new.your-care-recipients')}
						/>
					)}

					<AllStaffSelect onPress={setAllStaffSelected} selected={selectedAllStaff} />

					<TeamSelect
						selectedIds={selectedTeamIds ?? []}
						setSelectedIds={setSelectedTeamsIds}
						managedOnly
					/>

					{showRestrictVisibilityOption && selectedUsersIds.length > 0 && (
						<CheckBox checked={isInternal} onChange={setInternalOnly}>
							{t('post.new.restrict-visibility-checkbox')}
						</CheckBox>
					)}
				</View>
			) : isBlurFacesDisplayed ? (
				<BlurFaces
					images={selectedImages
						.map((media, mediaIndex) => {
							if (media.type !== 'image') {
								return undefined;
							}

							return { ...media, mediaIndex };
						})
						.filter(Boolean)}
					defaultReferenceImageUrl={defaultReferenceImage}
					blurredImages={blurredImages}
					setBlurredImages={setBlurredImages}
				/>
			) : isScheduleDisplayed ? (
				<Schedule formMethods={scheduleFormMethods} />
			) : (
				<>
					<View style={styles.userInfo}>
						<UserAvatar src={user.photoUrl} size={32} username={getName(user)} />
						<Text category="p2">{getName(user)}</Text>
					</View>
					{showPrivacy && (
						<PostPrivacyButtonWeb
							onPress={toggleSelectUsersDisplayed}
							selectedUsersInfoText={selectedUsersInfoText}
							disabled={saving}
							errors={errors.privacy}
						/>
					)}
					{showSchedule && (
						<ScheduleButtonWeb
							onPress={displaySchedule}
							disabled={saving}
							scheduleDate={scheduleDate}
						/>
					)}
					<AutoGrowInput
						placeholder={`${t('post.new.add-text')}...`}
						multiline
						rows={3}
						style={styles.input}
						textStyle={styles.inputText}
						errors={errors.content}
						error={errors.content.length > 0}
						{...contentInput}
					/>
					<ImagePickerCollage
						buttonStyle={styles.imageCollage}
						images={selectedImages}
						onAdd={onNewImage('gallery')}
						onRemove={onImageRemove}
						disabledNew={saving || selectedImages.length >= MAX_MEDIA_COUNT}
						actions={
							<Button
								color="basic"
								variant="filled"
								size="small"
								accessoryLeft={<BlurFaceIcon />}
								style={styles.imageCollage}
								onPress={toggleBlurFaces}
							>
								{t('post.blur-faces')}
							</Button>
						}
					/>
					<FilePicker
						files={pickedFiles}
						onAdd={pickFiles}
						onRemove={onRemoveFile}
						maxFiles={MAX_MEDIA_COUNT - selectedImages.length}
					/>
				</>
			)}
		</Modal>
	);
};

const styles = StyleSheet.create({
	// @ts-expect-error Valid for web
	modal: { maxHeight: '95vh' },
	userInfo: {
		flexDirection: 'row',
		gap: spacing(),
		alignItems: 'center',
		marginBottom: spacing(),
	},
	imageCollage: {
		marginTop: spacing(),
		// @ts-expect-error Valid for web
		width: 'fit-content',
	},
	publishButton: {
		width: '100%',
	},
	privacyButton: {
		flex: 1,
	},
	input: { marginBottom: spacing() },
	inputText: {
		maxHeight: 350,
	},
});
